import { useEffect, useState } from "react";
import type { RealmId, UserProfile } from "@chuzi/shared/types";
import { t } from "@chuzi/shared/config";
import { Button } from "@chuzi/shared/ui";
import { ChuziApiError } from "@chuzi/shared/api";
import { CosmosModal } from "./cosmos-modal";
import { DirectorAvatar } from "./director-avatar";
import { DirectorExperienceMapping } from "./director-experience-mapping";
import { api } from "../lib/api";

const FIELD_LABEL: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "rgba(232,240,255,0.55)",
  marginBottom: 4,
  display: "block",
};

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  background: "rgba(126,184,255,0.06)",
  border: "1px solid rgba(126,184,255,0.35)",
  borderRadius: 8,
  color: "#e8f0ff",
  padding: "8px 10px",
  fontSize: 13,
  outline: "none",
};

const REALM_OPTIONS: { id: RealmId; label: string }[] = [
  { id: "wilds", label: "CHUZI WILDS" },
  { id: "cosmos", label: "CHUZI COSMOS" },
];

/**
 * Director profile modal: edit name + experience realm. Avatar is shown as
 * a procedural seal — no upload UI yet (the V1 avatar endpoints land in the
 * follow-on); the realm switch changes the procedural avatar style live so
 * directors can preview the realm-specific seal.
 */
export function DirectorProfileModal({
  user,
  onClose,
  onUserUpdated,
}: {
  user: UserProfile | null;
  onClose: () => void;
  onUserUpdated: (user: UserProfile) => void;
}) {
  const [name, setName] = useState(user?.name ?? "");
  const [username, setUsername] = useState(user?.username ?? "");
  const [realm, setRealm] = useState<RealmId>(user?.realm ?? "cosmos");
  const [saving, setSaving] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameHint, setUsernameHint] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"profile" | "experienceMapping">(
    "profile",
  );
  const lexRealm = user?.realm ?? realm;

  useEffect(() => {
    setName(user?.name ?? "");
    setUsername(user?.username ?? "");
    setRealm(user?.realm ?? "cosmos");
    setActiveTab("profile");
    setUsernameHint(null);
  }, [user?.id, user?.name, user?.username, user?.realm]);

  const trimmedName = name.trim();
  const usernameLocked = Boolean(user?.username?.trim());
  const dirty =
    !!user &&
    (trimmedName !== (user.name ?? "")
      || realm !== (user.realm ?? "cosmos"));

  async function save() {
    if (!user || !dirty) return;
    if (!trimmedName) {
      setError(
        t(lexRealm, "director_profile_name_required", "Name is required."),
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let next: UserProfile = user;
      const nameChanged = trimmedName !== (user.name ?? "");
      const realmChanged = realm !== (user.realm ?? "cosmos");

      if (nameChanged) {
        const res = await api.user.updateProfile({ name: trimmedName });
        next = res.user;
      }
      if (realmChanged) {
        const res = await api.user.updateRealm({ realm });
        next = res.user;
      }
      // Optimistic merge: if one endpoint returns stale data for a field
      // updated by the other call, the locally-submitted values win.
      if (nameChanged) next = { ...next, name: trimmedName };
      if (realmChanged) next = { ...next, realm };
      onUserUpdated(next);
      onClose();
    } catch (err) {
      if (err instanceof ChuziApiError) {
        const body = err.body as
          | { message?: string; errors?: Record<string, string[]> }
          | null;
        const fieldErr = body?.errors
          ? Object.values(body.errors).flat()[0]
          : null;
        setError(
          fieldErr ??
            body?.message ??
            `${t(lexRealm, "director_profile_save_failed", "Save failed")} (${err.status})`,
        );
      } else {
        setError(
          err instanceof Error
            ? err.message
            : t(lexRealm, "director_profile_save_failed", "Save failed"),
        );
      }
      setSaving(false);
    }
  }

  const canSaveProfile = activeTab === "profile";
  const profileFooter = canSaveProfile ? (
    <>
      <Button
        label={t(lexRealm, "director_profile_close", "Close")}
        variant="ghost"
        onPress={onClose}
        disabled={saving}
      />
      <Button
        label={
          saving
            ? t(lexRealm, "director_profile_saving", "Saving…")
            : t(lexRealm, "director_profile_save", "Save")
        }
        onPress={save}
        disabled={saving || checkingUsername || !dirty}
      />
    </>
  ) : (
    <Button
      label={t(lexRealm, "director_profile_close", "Close")}
      variant="ghost"
      onPress={onClose}
    />
  );

  return (
    <CosmosModal
      title={t(lexRealm, "director_profile_title", "Director profile")}
      onClose={saving ? () => undefined : onClose}
      footer={profileFooter}
      width={user?.is_admin ? 1040 : 520}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <TabButton
            selected={activeTab === "profile"}
            label={t(lexRealm, "director_profile_tab_profile", "Profile")}
            onClick={() => setActiveTab("profile")}
          />
          {user?.is_admin ? (
            <TabButton
              selected={activeTab === "experienceMapping"}
              label={t(
                lexRealm,
                "director_profile_tab_experience_mapping",
                "Experience Mapping",
              )}
              onClick={() => setActiveTab("experienceMapping")}
            />
          ) : null}
        </div>

        {activeTab === "profile" ? (
          <div style={{ display: "flex", gap: 20 }}>
            <AvatarPreview seed={user?.id ?? "anonymous"} realm={realm} />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 14,
                minWidth: 0,
              }}
            >
              <div>
                <label style={FIELD_LABEL} htmlFor="director-name">
                  {t(lexRealm, "director_profile_name_label", "Name")}
                </label>
                <input
                  id="director-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  style={INPUT_STYLE}
                />
              </div>

              <div>
                <label style={FIELD_LABEL} htmlFor="director-username">
                  {t(lexRealm, "auth_username_placeholder", "Username")}
                </label>
                <input
                  id="director-username"
                  type="text"
                  value={username}
                  readOnly={usernameLocked}
                  onChange={(e) => {
                    if (usernameLocked) return;
                    setUsername(e.target.value);
                  }}
                  onBlur={() => {
                    if (usernameLocked || !username.trim() || !user) return;
                    if (username.trim().toLowerCase() === (user.username ?? "").trim().toLowerCase()) {
                      setUsernameHint(
                        t(lexRealm, "auth_username_available", "Username is available."),
                      );
                      return;
                    }
                    setCheckingUsername(true);
                    setError(null);
                    void api.auth
                      .usernameAvailability(username.trim())
                      .then((availability) => {
                        if (availability.available) {
                          setUsernameHint(
                            t(lexRealm, "auth_username_available", "Username is available."),
                          );
                          return;
                        }
                        const suggestion =
                          availability.suggestion?.trim() ||
                          `${username.trim()}${Math.random().toString(36).slice(2, 3) || "x"}`;
                        setUsername(suggestion);
                        setUsernameHint(
                          t(
                            lexRealm,
                            "auth_username_unavailable",
                            "Username is taken; updated to an available suggestion.",
                          ),
                        );
                      })
                      .catch((err) => {
                        setError(
                          err instanceof Error
                            ? err.message
                            : t(
                                lexRealm,
                                "auth_username_update_failed",
                                "Could not reserve a unique username.",
                              ),
                        );
                      })
                      .finally(() => setCheckingUsername(false));
                  }}
                  maxLength={120}
                  style={{
                    ...INPUT_STYLE,
                    ...(usernameLocked
                      ? {
                          opacity: 0.72,
                          cursor: "not-allowed",
                          background: "rgba(126,184,255,0.03)",
                        }
                      : {}),
                  }}
                />
                {usernameLocked ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: "rgba(232,240,255,0.45)",
                    }}
                  >
                    {t(
                      lexRealm,
                      "director_profile_username_locked_hint",
                      "Username is set at sign up and cannot be changed.",
                    )}
                  </div>
                ) : usernameHint ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: "rgba(138,240,186,0.9)",
                    }}
                  >
                    {usernameHint}
                  </div>
                ) : null}
              </div>
              <div>
                <span style={FIELD_LABEL}>
                  {t(
                    lexRealm,
                    "director_profile_realm_label",
                    "Experience realm",
                  )}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  {REALM_OPTIONS.map((opt) => (
                    <RealmRadio
                      key={opt.id}
                      selected={realm === opt.id}
                      label={opt.label}
                      onClick={() => setRealm(opt.id)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <span style={FIELD_LABEL}>
                  {t(lexRealm, "director_profile_image_label", "Image")}
                </span>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(232,240,255,0.6)",
                  }}
                >
                  {t(
                    lexRealm,
                    "director_profile_image_hint",
                    "Realm-themed seal — switching the realm above swaps the seal. Custom uploads land with the V1 avatar endpoints.",
                  )}
                </div>
              </div>

              {error ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "#ff9aa2",
                    background: "rgba(255,154,162,0.08)",
                    border: "1px solid rgba(255,154,162,0.4)",
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                >
                  {error}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {activeTab === "experienceMapping" && user ? (
          <DirectorExperienceMapping user={user} />
        ) : null}
      </div>
    </CosmosModal>
  );
}

function AvatarPreview({ seed, realm }: { seed: string; realm: RealmId }) {
  return (
    <div
      style={{
        width: 120,
        height: 120,
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        border: "1px solid rgba(126,184,255,0.45)",
        background: "rgba(126,184,255,0.08)",
        padding: 12,
        boxSizing: "border-box",
      }}
    >
      <DirectorAvatar seed={seed} realm={realm} />
    </div>
  );
}

function RealmRadio({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        flex: 1,
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${
          selected ? "rgba(126,184,255,0.85)" : "rgba(126,184,255,0.3)"
        }`,
        background: selected
          ? "rgba(126,184,255,0.18)"
          : "rgba(126,184,255,0.04)",
        color: "#e8f0ff",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.5,
        cursor: "pointer",
        textAlign: "center",
        boxShadow: selected
          ? "0 0 0 2px rgba(126,184,255,0.35) inset"
          : "none",
      }}
    >
      {label}
    </button>
  );
}

function TabButton({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        padding: "8px 12px",
        borderRadius: 10,
        border: `1px solid ${
          selected ? "rgba(126,184,255,0.85)" : "rgba(126,184,255,0.3)"
        }`,
        background: selected
          ? "rgba(126,184,255,0.18)"
          : "rgba(126,184,255,0.04)",
        color: "#e8f0ff",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.35,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
